/**
 * useAuthActions — tiny hook for logout + related auth flows in feature UI.
 *
 * Wraps `performLogout` so the AppShell / user menu / session-expired handler
 * all share one code path. The hook resolves the required collaborators
 * (api bundle, token storage, query client) from the surrounding providers,
 * so callers only need the returned function handles.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@shopista/shared/api/hooks';
import { performLogout } from './logout';
import { useTokens } from './useTokens';
import { routes } from '../routes/paths';

export interface UseAuthActions {
  logout: () => Promise<void>;
}

export function useAuthActions(): UseAuthActions {
  const api = useApi();
  const tokens = useTokens();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const logout = useCallback(async () => {
    await performLogout({ api, tokens, queryClient });
    navigate(routes.login, { replace: true });
  }, [api, tokens, queryClient, navigate]);

  return { logout };
}
